import { useTranslation } from 'react-i18next';
import type { SafetyEvent } from '@apis/reports';
import { PanelCard } from '../../shared/PanelCard/PanelCard';
import { DataRow } from '../../shared/DataRow/DataRow';
import type { IPanelQuery } from '../../shared/types';
import classes from './SafetyEventsPanel.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface ISafetyEventsPanelProps {
  query: IPanelQuery<SafetyEvent[]>;
}

export function SafetyEventsPanel({ query }: ISafetyEventsPanelProps) {
  const { t } = useTranslation();
  const title = t('reports.observeLocation.section.safetyEvents');

  if (query.isLoading) {
    return (
      <PanelCard aria-busy="true" aria-label={title}>
        <h4 className={classes.title}>{title}</h4>
      </PanelCard>
    );
  }

  if (query.isError) {
    return (
      <PanelCard role="alert" aria-label={title}>
        <h4 className={classes.title}>{title}</h4>
        <div className={classes.errorWrap}>
          {t('reports.observeLocation.errorRetry')}
          <button
            type="button"
            className={classes.retry}
            onClick={() => query.refetch()}
          >
            {t('reports.observeLocation.retry')}
          </button>
        </div>
      </PanelCard>
    );
  }

  const items = query.data ?? [];

  return (
    <PanelCard aria-label={title}>
      <h4 className={classes.title}>{title}</h4>
      {items.length === 0 ? (
        <div className={classes.empty}>
          {t('reports.observeLocation.empty.safetyEvents')}
        </div>
      ) : (
        <ScrollArea className={classes.listScroll}>
          <ul className={classes.list}>
            {items.map((e) => {
              const dotClass = e.isCritical
                ? `${classes.dot} ${classes.dotCritical}`
                : classes.dot;
              const label = t(`safety.violations.${e.safetyViolationType}`, {
                defaultValue: e.safetyViolationType,
              });
              const chip = e.trade
                ? {
                    text: e.trade.name,
                    ...(e.trade.backgroundColor
                      ? { backgroundColor: e.trade.backgroundColor }
                      : {}),
                  }
                : undefined;
              return (
                <DataRow
                  key={e.safetyEventId}
                  label={label}
                  leadingIcon={<span className={dotClass} aria-hidden="true" />}
                  {...(chip ? { chip } : {})}
                />
              );
            })}
          </ul>
        </ScrollArea>
      )}
    </PanelCard>
  );
}
